/* vp-plan-guard.js -- keep back-office selling plans off the storefront.
 *
 * A shopper may only ever be offered the main Subscribe & Save cadences for the
 * size they are buying. The two templates want different sets:
 *
 *                       builder (claude-4)      standard PDP (offer-pre-order)
 *     15-serving bag    21 / 32 / 45 days       21 / 32 days
 *     25-serving bag    20 / 40 / 60 days       20 / 40 days
 *
 * The 15-serving ladder was 15/30/45 until 2026-09-22. Those two short cadences
 * churned hard -- a 15-serving bag arriving every 15 days outruns how fast most
 * people drink it -- so one bag moved to 21 days and two to 32. The pre-order PDP
 * kept selling the retired 15 and 30 for two more days, which is how order
 * #404288 shipped a bag on a 15-day cadence the builder can no longer produce.
 * It now offers 21/32 as well, so BOTH surfaces sell the same intervals and a
 * retired cadence cannot be bought anywhere.
 *
 * That the two ladders now coincide has a second consequence, noted here because
 * it is easy to miss: cadence alone no longer identifies which surface a line was
 * bought on. The Rebuy cart used to treat "a 15-serving bag on 15 or 30 days" as
 * a hand-picked pre-order choice and leave it alone; with 15 and 30 withdrawn
 * there is nothing left to protect, and that exemption has been removed so mixed
 * carts consolidate again. If a per-surface exemption is ever wanted back, it
 * needs the template to TAG its lines (the GWP already does this with
 * _gwp_item) rather than inferring it from the interval.
 *
 * Everything else goes, and in particular ANY plan whose name carries
 * "(Aftersell)". Those exist for post-purchase offers and are priced at FULL
 * LIST rather than the subscriber rate:
 *
 *     Aftersell "every 45 days"   $49.99 / bag
 *     main      "every 60 days"   $39.99 / bag
 *
 * On the builder that mattered a lot. It picks the plan NEAREST a target
 * cadence (cadence_by_bags "1:17, 2:36, 3:52"), and at the 3-bag target of 52
 * days the Aftersell 45 beat the main 60 by a single day -- so a 3-bag
 * subscription billed $135.00 instead of $108.00, and renewed at that price
 * every 45 days. Dropping the back-office plans is what fixes it.
 *
 * WHY THE CADENCE TARGETS ARE LEFT AT 17/36/52 rather than rewritten to the new
 * 21/32/45. The targets are shared by BOTH sizes and only the allow-list is per
 * size, so nearest-match resolves them differently for each:
 *
 *     target 17 -> 15-serving 21, 25-serving 20
 *     target 36 -> 15-serving 32, 25-serving 40
 *     target 52 -> 15-serving 45, 25-serving 60
 *
 * Retuning the targets to 21/32/45 would land the 25-serving 3-bag tier on 40
 * instead of 60 (|45-40| beats |45-60|), quietly halving that cadence. The
 * allow-list is the safe lever; the targets are not.
 *
 * Also removed: the "{inactive frequency}" group (25/50/70-day, 3- and 6-month,
 * 8-week). Not meant to be sold, and its 50-day was pulling the 3-bag choice
 * off 45 for two flavours.
 *
 * SIZE CANNOT BE INFERRED FROM THE CADENCES ON OFFER. A 25-serving variant such
 * as IHOP Cinn-A-Stack also belongs to the group that carries the 15-day plan,
 * so "offers a 15-day plan" does not mean "is a 15-serving bag". The real size
 * lives in the variant title; see sizeFromTitle below for the two shapes that
 * takes. The builder states it outright in its own data; the PDP reads it from
 * the product JSON.
 */
(function () {
  "use strict";

  var BLOCKED_NAME  = /aftersell/i;
  var BLOCKED_GROUP = /inactive/i;
  var BUILDER_ALLOW = { "15": [21, 32, 45], "25": [20, 40, 60] };
  var PDP_ALLOW     = { "15": [21, 32],     "25": [20, 40]     };

  function days(label) {
    var m = String(label || "").match(/every\s+(\d+)\s*days?/i);
    return m ? parseInt(m[1], 10) : null;
  }

  /* Bag size from a variant title, written to survive the option restructure.
     Until the split the Size option carried a single value ("25 Servings") and the
     real size hid in the Flavor parentheses -- "... (15 Servings)". Breaking
     15-serving out into its own Size value makes the title "15 Servings / Flavor",
     with no parentheses anywhere, so a regex that REQUIRES them would silently size
     every 15-serving bag as 25 and hand it the 20/40/60 ladder. Matching the words
     anywhere in the title is true of both shapes, so the rollout can happen in
     either order. "25 Servings" never contains "15 servings", so there is no
     false positive. */
  function sizeFromTitle(title) {
    return /\b15\s*servings?\b/i.test(String(title || "")) ? "15" : "25";
  }

  /* ---- surface 1: the bundle builder -------------------------------------
     The builder reads #vp-data, and that data already carries each variant's
     size. vp-pdp-2.js is DEFERRED, so this file is loaded WITHOUT defer and
     rewrites the JSON while the parser is still working through the page --
     before the builder ever sees it. */
  function guardBuilderData() {
    var el = document.getElementById("vp-data");
    if (!el || el.getAttribute("data-plan-guard")) return;

    var d;
    try { d = JSON.parse(el.textContent); } catch (e) { return; }
    if (!d || !d.variants) return;
    var groups = d.planGroups || {};

    d.variants.forEach(function (v) {
      var plans = v.plans || [];
      var allow = BUILDER_ALLOW[String(v.size)];

      var kept = plans.filter(function (p) {
        if (BLOCKED_NAME.test(p.name)) return false;
        if (BLOCKED_GROUP.test(groups[p.group] || "")) return false;
        var n = days(p.name);
        return allow ? allow.indexOf(n) !== -1 : n !== null;
      });

      /* Never strand a variant with no purchase option. If the size rule would
         empty it, fall back to dropping only the back-office plans. */
      if (!kept.length) {
        kept = plans.filter(function (p) {
          return !BLOCKED_NAME.test(p.name) &&
                 !BLOCKED_GROUP.test(groups[p.group] || "");
        });
      }
      v.plans = kept;
    });

    el.textContent = JSON.stringify(d);
    el.setAttribute("data-plan-guard", "1");
  }

  /* ---- surface 2: Loop's selectors on the standard PDP -------------------
     Loop renders one <select> per variant PER GROUP, server-side, each with
     id "loop-select-<variantId>-<groupHash>" -- so one variant owns several,
     and a 25-serving variant can own one belonging to a 15-day group. Any
     select left with no options is hidden: an empty dropdown reads worse than
     no dropdown at all. */
  var sizeByVariant = null;

  function loadSizes(done) {
    if (sizeByVariant) return done();
    var path = location.pathname.split("?")[0].replace(/\/+$/, "");
    if (!/\/products\//.test(path)) { sizeByVariant = {}; return done(); }
    fetch(path + ".js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (p) {
        sizeByVariant = {};
        (p.variants || []).forEach(function (v) {
          /* SKU is the last resort and the most restructure-proof signal there
             is: every 15-serving bag already ends in -15. */
          sizeByVariant[v.id] = /-15$/i.test(v.sku || "")
            ? "15"
            : sizeFromTitle(v.title);
        });
        done();
      })
      .catch(function () { sizeByVariant = {}; });
  }

  function guardLoopSelects() {
    var sels = document.querySelectorAll("select.loop-selling-plan-selector");
    if (!sels.length || !sizeByVariant) return;

    [].forEach.call(sels, function (s) {
      var m = (s.id || "").match(/loop-select-(\d+)/);
      if (!m) return;
      var allow = PDP_ALLOW[sizeByVariant[m[1]]];
      if (!allow) return;                      /* unknown variant: leave alone */

      var doomed = [].filter.call(s.options, function (o) {
        if (BLOCKED_NAME.test(o.textContent || "")) return true;
        var n = days(o.textContent);
        return n === null || allow.indexOf(n) === -1;
      });
      if (!doomed.length) return;

      var was = s.value;
      doomed.forEach(function (o) { o.parentNode.removeChild(o); });

      if (!s.options.length) { s.style.display = "none"; return; }
      if (s.value !== was) s.selectedIndex = 0;
    });
  }

  function run() {
    try { guardBuilderData(); } catch (e) {}
    try { loadSizes(function () { try { guardLoopSelects(); } catch (e) {} }); } catch (e) {}
  }

  run();
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  }

  /* Loop re-renders its selectors on variant change. Debounced, and converges:
     once the options are gone there is nothing left to remove. */
  var t;
  new MutationObserver(function () {
    clearTimeout(t);
    t = setTimeout(run, 50);
  }).observe(document.documentElement, { childList: true, subtree: true });
})();